import { 
  Stethoscope, BarChart3, 
  Shield, Database, Zap, CheckCircle, TrendingUp, Lock, Globe, Clock,
  Target, Users, Award, Eye, AlertTriangle, FileCheck, UserCheck, Factory
} from 'lucide-react';
import { ProductConfigs } from './types';

export const industrySolutionsConfigs: ProductConfigs = {
  'healthcare-practice-management': {
    keyBenefits: [
      {
        icon: Stethoscope,
        title: 'Integrated EHR Management',
        description: 'Comprehensive electronic health records with clinical decision support improving patient care by 60%.',
        metrics: '60% care improvement',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Users,
        title: 'Patient Engagement Platform',
        description: 'Patient portal with appointment scheduling, secure messaging, and health monitoring tools.',
        metrics: 'Patient engagement',
        color: 'from-blue-500 to-purple-500'
      },
      {
        icon: Target,
        title: 'Revenue Cycle Optimization',
        description: 'Automated billing and insurance claims processing reducing claim denials by 80%.',
        metrics: '80% denial reduction',
        color: 'from-green-500 to-blue-500'
      },
      {
        icon: BarChart3,
        title: 'Clinical Workflow Management',
        description: 'Streamlined clinical workflows with task automation improving provider efficiency by 45%.',
        metrics: '45% efficiency boost',
        color: 'from-purple-500 to-pink-500'
      },
      {
        icon: Shield,
        title: 'HIPAA Compliance & Security',
        description: 'Complete HIPAA compliance with advanced security features and audit trail management.',
        metrics: 'HIPAA compliant',
        color: 'from-red-500 to-orange-500'
      },
      {
        icon: Award,
        title: 'Quality Measures Tracking',
        description: 'Automated quality reporting with population health management and outcome tracking.',
        metrics: 'Quality tracking',
        color: 'from-cyan-500 to-blue-500'
      }
    ],
    advancedCapabilities: [
      {
        icon: Eye,
        title: 'AI Clinical Decision Support',
        description: 'Machine learning provides clinical recommendations and alerts improving diagnostic accuracy.',
        metrics: 'AI decision support',
        color: 'from-indigo-500 to-purple-500'
      },
      {
        icon: AlertTriangle,
        title: 'Predictive Health Analytics',
        description: 'Advanced analytics identify at-risk patients and predict health outcomes proactively.',
        metrics: 'Predictive analytics',
        color: 'from-red-500 to-pink-500'
      },
      {
        icon: Database,
        title: 'Interoperability Platform',
        description: 'Seamless integration with labs, hospitals, and health information exchanges.',
        metrics: 'Full interoperability',
        color: 'from-blue-500 to-cyan-500'
      },
      {
        icon: Factory,
        title: 'Telemedicine Integration',
        description: 'Built-in telehealth capabilities with video consultations and remote patient monitoring.',
        metrics: 'Telemedicine ready',
        color: 'from-green-500 to-emerald-500'
      },
      {
        icon: FileCheck,
        title: 'Advanced Reporting Suite',
        description: 'Comprehensive reporting with clinical outcomes, financial performance, and regulatory compliance.',
        metrics: 'Advanced reporting',
        color: 'from-purple-500 to-indigo-500'
      },
      {
        icon: UserCheck,
        title: 'Population Health Management',
        description: 'Population health tools with risk stratification and care gap identification.',
        metrics: 'Population health',
        color: 'from-yellow-500 to-orange-500'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, and GDPR compliance with automated audit trails and regulatory reporting.',
        metrics: 'SOC 2, ISO 27001, GDPR',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Real-time threat analysis and security posture optimization with continuous monitoring dashboards.',
        metrics: 'Real-time threat analysis',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade cloud infrastructure with auto-scaling security controls and global redundancy.',
        metrics: 'Enterprise-grade platform',
        color: 'from-blue-500 to-green-600'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'AI-powered threat detection with automatic mitigation and advanced persistent threat protection.',
        metrics: 'AI-powered protection',
        color: 'from-red-500 to-orange-600'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO integration with biometric authentication and risk-based access controls.',
        metrics: 'Enterprise SSO + Biometric',
        color: 'from-teal-500 to-blue-600'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with machine learning anomaly detection and automated incident response.',
        metrics: 'Real-time SIEM + ML',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification with real-time DLP monitoring and encrypted backup systems.',
        metrics: 'Automated DLP + Backup',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with continuous network monitoring and zero-trust architecture implementation.',
        metrics: 'Zero-trust architecture',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning with penetration testing and security posture management.',
        metrics: '24/7 vulnerability scanning',
        color: 'from-orange-500 to-red-600'
      }
    ]
  },

  'financial-services-platform': {
    keyBenefits: [
      {
        icon: BarChart3,
        title: 'Comprehensive Portfolio Management',
        description: 'Advanced portfolio analytics with risk assessment reducing investment losses by 40%.',
        metrics: '40% loss reduction',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Users,
        title: 'Client Relationship Management',
        description: 'Integrated CRM with client communication and relationship tracking improving retention by 65%.',
        metrics: '65% retention boost',
        color: 'from-blue-500 to-purple-500'
      },
      {
        icon: Shield,
        title: 'Regulatory Compliance Automation',
        description: 'Automated compliance monitoring and reporting ensuring 100% regulatory adherence.',
        metrics: '100% compliance',
        color: 'from-green-500 to-blue-500'
      },
      {
        icon: Target,
        title: 'Risk Assessment & Analysis',
        description: 'Advanced risk modeling with stress testing and scenario analysis for portfolio optimization.',
        metrics: 'Risk optimization',
        color: 'from-red-500 to-pink-500'
      },
      {
        icon: TrendingUp,
        title: 'Investment Research Platform',
        description: 'Comprehensive investment research with market analysis and performance benchmarking.',
        metrics: 'Research platform',
        color: 'from-orange-500 to-yellow-500'
      },
      {
        icon: Award,
        title: 'Fee Management & Billing',
        description: 'Automated fee calculations with transparent billing and performance-based pricing models.',
        metrics: 'Automated billing',
        color: 'from-cyan-500 to-blue-500'
      }
    ],
    advancedCapabilities: [
      {
        icon: Eye,
        title: 'AI-Powered Market Analysis',
        description: 'Machine learning algorithms analyze market trends providing predictive investment insights.',
        metrics: 'AI market analysis',
        color: 'from-indigo-500 to-purple-500'
      },
      {
        icon: AlertTriangle,
        title: 'Real-Time Risk Monitoring',
        description: 'Continuous portfolio monitoring with automated alerts for risk thresholds and market volatility.',
        metrics: 'Real-time monitoring',
        color: 'from-red-500 to-pink-500'
      },
      {
        icon: Database,
        title: 'Trading Platform Integration',
        description: 'Seamless integration with major trading platforms and market data providers.',
        metrics: 'Trading integration',
        color: 'from-blue-500 to-cyan-500'
      },
      {
        icon: Factory,
        title: 'Alternative Investment Management',
        description: 'Specialized tools for private equity, hedge funds, and alternative investment strategies.',
        metrics: 'Alternative investments',
        color: 'from-green-500 to-emerald-500'
      },
      {
        icon: FileCheck,
        title: 'Advanced Performance Analytics',
        description: 'Comprehensive performance attribution analysis with custom benchmarking and reporting.',
        metrics: 'Performance analytics',
        color: 'from-purple-500 to-indigo-500'
      },
      {
        icon: UserCheck,
        title: 'Client Portal & Mobile Access',
        description: 'White-label client portal with mobile access for real-time portfolio viewing and reporting.',
        metrics: 'Client portal',
        color: 'from-yellow-500 to-orange-500'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, and GDPR compliance with automated audit trails and regulatory reporting.',
        metrics: 'SOC 2, ISO 27001, GDPR',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Real-time threat analysis and security posture optimization with continuous monitoring dashboards.',
        metrics: 'Real-time threat analysis',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade cloud infrastructure with auto-scaling security controls and global redundancy.',
        metrics: 'Enterprise-grade platform',
        color: 'from-blue-500 to-green-600'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'AI-powered threat detection with automatic mitigation and advanced persistent threat protection.',
        metrics: 'AI-powered protection',
        color: 'from-red-500 to-orange-600'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO integration with biometric authentication and risk-based access controls.',
        metrics: 'Enterprise SSO + Biometric',
        color: 'from-teal-500 to-blue-600'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with machine learning anomaly detection and automated incident response.',
        metrics: 'Real-time SIEM + ML',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification with real-time DLP monitoring and encrypted backup systems.',
        metrics: 'Automated DLP + Backup',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with continuous network monitoring and zero-trust architecture implementation.',
        metrics: 'Zero-trust architecture',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning with penetration testing and security posture management.',
        metrics: '24/7 vulnerability scanning',
        color: 'from-orange-500 to-red-600'
      }
    ]
  }
};